import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import ApiCaller from '../services/ApiCaller';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import PortalConfiguration from '../configuration/config';
import CampaignFabricatorAuthTokens from '../configuration/AuthorizationTokens';
import useAuthorization from '../hooks/useAuthorization';

const AuthContext = createContext();

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [username, setUsername] = useState(localStorage.getItem('username') || "");
    const [sessionID, setSessionID] = useState(localStorage.getItem('sessionID') || '');
    const [sessionIDChecked, setSessionIDChecked] = useState(null);
    const [loggedIn, setLoggedIn] = useState(false);  // Initially false until checked
    const [authorizationProfile, setAuthorizationProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showLogin, setShowLogin] = useState(false);
    const [userPermissionLevel, setUserPermissionLevel] = useState(CampaignFabricatorAuthTokens.ANONYMOUS);
    const navigate = useNavigate();
    const { checkAuthorization } = useAuthorization(); 

    // Initialize the API Caller
    const apiCaller = useState(new ApiCaller(PortalConfiguration.usersettingsURL))[0];

    const saveToLocalStorage = (key, value) => {
        if (value) {
            localStorage.setItem(key, value);
        } else {
            localStorage.removeItem(key);
        }
    };

    useEffect(() => {
        saveToLocalStorage('username', username);
    }, [username]);

    useEffect(() => {
        saveToLocalStorage('sessionID', sessionID);
    }, [sessionID]);

    const login = useCallback(async (inputUsername, password) => {
        if (loading) return;

        setLoading(true);
        setError(null);

        try {
            const result = await apiCaller.callApi({
                authorization: '',
                userID: inputUsername,
                service: '/login',
                method: 'PUT',
                headers: {
                    'applicationID': '08-08-2024AOCF',
                },
                body: { username: inputUsername, password, applicationID: '08-08-2024AOCF' },
            });

            if (result.message === 'Login successful') {
                setUsername(inputUsername);
                setSessionID(result.sessionID);
                setSessionIDChecked(Date.now());
                setAuthorizationProfile(result.authorizationProfile);
                setLoggedIn(true);
            } else {
                throw new Error(result.message || 'Login failed');
            }
        } catch (err) {
            ApiCaller.handleApiError(err);
            setLoggedIn(false);
        } finally {
            setLoading(false);
        }
    }, [apiCaller, loading, navigate]);

    const changePassword = useCallback(async (newPassword, newPasswordVerification) => {
        if (loading) return;

        setLoading(true);
        setError(null);
        var result;
        try {
            if (newPassword !== newPasswordVerification)
            {
                result = {message: 'Passwords do not match'};
            } else
            {
                result = await apiCaller.callApi({
                    authorization: sessionID,
                    userID: username,
                    service: '/user/changepassword',
                    method: 'PUT',
                    headers: {
                        'applicationID': PortalConfiguration.defaultApplicationID,
                    },
                    body: {userdata: { username, password: newPassword} },
                });
            }
            if (result.message === 'Password updated successfully') {
                message.success('Password changed successfully');
            } else {
                message.error('Password change failed')
                throw new Error(result.message || 'Password change failed');
            }
        } catch (err) {
            ApiCaller.handleApiError(err);
        } finally {
            setLoading(false);
        }
    }, [apiCaller, loading, sessionID, username]);

    const logout = useCallback(() => {
        setSessionID('');
        setUsername('');
        setLoggedIn(false);
    }, [navigate]);

    const checkSession = useCallback(async () => {
        if (!sessionID) return false;
        if (sessionID && sessionIDChecked > Date.now() - 3000) return true; // Check every 3 seconds

        setLoading(true);
        setError(null);

        try {
            const result = await apiCaller.callApi({
                authorization: sessionID,
                userID: username,
                service: '/checkSession',
                method: 'PUT',
                headers: {
                    'applicationID': '08-08-2024AOCF',
                },
                body: { sessionID }
            });

            if (result.message === 'Session valid') {
                setLoggedIn(true);
                setUsername(result.usersession.userId || username); // Set username if returned, otherwise keep existing
                setAuthorizationProfile(result.usersession.authorizationProfile);
                setSessionIDChecked(Date.now());
                return true;
            } else {
                throw new Error('Session invalid');
            }
        } catch (err) {
            ApiCaller.handleApiError(err);
            setLoggedIn(false);
            setSessionID('');
            setUsername('');
            return false;
        } finally {
            setLoading(false);
        }
    }, [sessionID, apiCaller, username, navigate]);

    const isSessionValid = useCallback(async () => {
        
        return await checkSession();
    }, [checkSession]);

    useEffect(() => {
        async function fetchData() {
            setUserPermissionLevel(CampaignFabricatorAuthTokens.ANONYMOUS);
            if (authorizationProfile) {
                const permissionLevels = CampaignFabricatorAuthTokens.allTokens ;
                for (const { name, value } of permissionLevels) {
                    if (await checkAuthorization(sessionID, value, authorizationProfile)) {
                        setUserPermissionLevel(value);
                        break; // Exit the loop after setting the permission level
                    }
                }
            }
        }
        fetchData();
    }, [checkAuthorization, authorizationProfile, sessionID]);
    
    // Check the session on initial load
    useEffect(() => {
        if (username && sessionID) {
            checkSession();
            
        }
    }, [username, sessionID, checkSession]);

    return (
        <AuthContext.Provider value={{
            username,
            loggedIn,
            sessionID,
            loading,
            error,
            login,
            logout,
            checkSession,
            isSessionValid,  // Add isSessionValid to the context value
            changePassword,
            userPermissionLevel,
            showLogin, 
            setShowLogin
        }}>
            {children}
        </AuthContext.Provider>
    );
};
